import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import type { PlainRunner } from "@/queries/runner-queries";
import {
    routeForProgress,
    useRunnerConfigurationProgress,
} from "@/routes/onboarding/use-runner-configuration-progress";
import { useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { DeleteRunnerModal } from "@/components/runners/DeleteRunnerModal";
import { useCurrentMember } from "@/hooks/use-current-member";
import { useMember } from "@/hooks/use-member";
import { RunnerName } from "@/components/runners/RunnerName";
import { AWSLogo } from "@/assets/logos/AWSLogo";
import { LinuxLogo } from "@/assets/logos/LinuxLogo";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";

export const ContinueWithExistingRunnerCard: FC<{
    runner: PlainRunner;
}> = ({ runner }) => {
    const [showDeletionModal, setShowDeletionModal] = useState(false);

    const { progress } = useRunnerConfigurationProgress(runner.runnerId);
    const to = routeForProgress(progress);
    const disabled = !to;

    const { currentMember } = useCurrentMember();
    const { member: creatorMember } = useMember(runner.creator?.id);

    const createdByLabel = useMemo(() => {
        return currentMember?.userId === creatorMember?.userId ? "You" : creatorMember?.fullName;
    }, [creatorMember, currentMember]);

    const Logo = useMemo(() => {
        switch (runner.provider) {
            case RunnerProvider.LINUX_HOST:
                return LinuxLogo;
            case RunnerProvider.AWS_EC2:
                return AWSLogo;
            default:
                return AWSLogo;
        }
    }, [runner.provider]);

    return (
        <>
            <div className="flex items-center justify-between gap-4 overflow-x-hidden rounded-xl border-[0.5px] border-border-base px-5 py-4">
                {/* Content */}
                <div className="flex min-w-0 shrink gap-4">
                    <Logo className="size-8 shrink-0" />
                    <div className="flex shrink flex-col overflow-x-hidden">
                        <RunnerName name={runner.name} />
                        {createdByLabel && (
                            <Text className="min-w-0 truncate text-sm text-content-secondary">
                                Setup started by {createdByLabel}
                            </Text>
                        )}
                    </div>
                </div>
                {/* Buttons */}
                <div className="flex shrink-0 gap-2">
                    <Button variant="primary" disabled={disabled} asChild>
                        <Link
                            className={cn({ "pointer-events-none": disabled })}
                            to={to || ""}
                            aria-disabled={disabled}
                            aria-label={`Continue with ${runner.name}`}
                        >
                            Continue
                        </Link>
                    </Button>
                    <Button variant="secondary" LeadingIcon={IconTrash} onClick={() => setShowDeletionModal(true)} />
                </div>
            </div>
            <DeleteRunnerModal runner={runner} onClose={() => setShowDeletionModal(false)} open={showDeletionModal} />
        </>
    );
};
